import {
  ADAPTIVE_COMPOSITIONS_LABS_FEATURE_ID,
  WHATSAPP_CHANNEL_LABS_FEATURE_ID,
} from '@syncanix/shared';
import { useMemo } from 'react';

import { useExperimentalFeature } from '@/features/experimental-features';

import type { WiredSidebarLabsFlag } from './sidebar-labs-flags';

/**
 * The resolved state of every Labs flag the app gates a NAV ROW on.
 *
 * Extracted from `useSidebarItems` when a second consumer arrived: the settings
 * search gates a setting on the same flag its page lives behind, and reading
 * that flag independently would let the two disagree — a setting offered for a
 * page the sidebar hides, which is the hole the gate was written to close.
 *
 * It cannot loop. Each flag needs its own `useExperimentalFeature` call, and
 * hooks may not run in a loop, so the record is written by hand — which is
 * exactly why the return type is an EXACT `Record` over the wired-flag union
 * rather than `Record<string, boolean>`. Both halves of the coupling are then
 * compile errors: a flag named in the union but not resolved here leaves the
 * record short, and one resolved here but absent from the union is not a
 * member. That is the guard that would have caught the former `/site-manual`
 * row shipping permanently hidden.
 *
 * Fail-CLOSED by `useExperimentalFeature`: false while loading, false on error,
 * false for an id the server does not list.
 */
export function useWiredLabsFlags(): Readonly<Record<WiredSidebarLabsFlag, boolean>> {
  const compositionsEnabled = useExperimentalFeature(ADAPTIVE_COMPOSITIONS_LABS_FEATURE_ID);
  const whatsappEnabled = useExperimentalFeature(WHATSAPP_CHANNEL_LABS_FEATURE_ID);
  // Memoised because both consumers put this record in a `useMemo` dependency
  // list: a fresh object each render would make those memos re-run on every
  // render, which for the search means rebuilding the whole index per keystroke.
  return useMemo(
    () => ({
      [ADAPTIVE_COMPOSITIONS_LABS_FEATURE_ID]: compositionsEnabled,
      [WHATSAPP_CHANNEL_LABS_FEATURE_ID]: whatsappEnabled,
    }),
    [compositionsEnabled, whatsappEnabled],
  );
}
